import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputText } from 'primereact/inputtext';
import { Badge } from 'primereact/badge';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import withRouter from '../lib/withRouter';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './PrimaryContacts.scss';

class PrimaryContacts extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();

        this.state = {
            loading: false,
            groupedContacts: [],
            searchTerm: '',
            expandedAccordion: null
        };
    }

    componentDidMount() {
        this.fetchPrimaryContacts();
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({
            severity: severity,
            summary: summary,
            detail: detail,
            life: 3000
        });
    }

    fetchPrimaryContacts = async () => {
        const academicYear = localStorage.getItem('userAcademicYear');

        if (!academicYear) {
            this.showToast('error', 'Error', 'Missing academic year');
            return;
        }

        this.setState({ loading: true });

        try {
            const url = `${baseUrlAdmin}/fee-decision/parent-profiles/primary-contacts?academicYear=${academicYear}`;
            const response = await this.service.get(url, true);

            if (response?.status && response?.res?.success) {
                this.setState({
                    groupedContacts: response.res.data || []
                });
            } else {
                this.showToast('error', 'Error', 'Failed to fetch primary contacts');
            }
        } catch (error) {
            console.error('Error fetching primary contacts:', error);
            this.showToast('error', 'Error', 'Failed to fetch primary contacts');
        } finally {
            this.setState({ loading: false });
        }
    }

    handleAccordionChange = (e) => {
        this.setState({ expandedAccordion: e.index });
    }

    navigateToPayerProfiles = () => {
        const { navigate } = this.props;
        navigate('/financeNew/PayerProfiles');
    }

    // Template Methods
    getProfessionSeverity = (category) => {
        switch (category) {
            case 'SALARIED': return 'success';
            case 'BUSINESS': return 'warning';
            case 'FARMER': return 'info';
            case 'PROFESSIONAL': return 'primary';
            case 'DAILY_WAGE': return 'danger';
            default: return 'secondary';
        }
    }

    professionBodyTemplate = (rowData) => {
        if (!rowData.professionCategory) return <span className="text-muted">Not Set</span>;
        
        const severity = this.getProfessionSeverity(rowData.professionCategory);
        const details = rowData.professionDetails || {};
        
        return (
            <div>
                <Tag value={details.label || rowData.professionCategory} severity={severity} />
            </div>
        );
    }

    cashFlowBodyTemplate = (rowData) => {
        const details = rowData.professionDetails || {};
        return (
            <div className="cash-flow-pattern">
                {details.cashFlowPattern || 'UNKNOWN'}
            </div>
        );
    }

    timingBodyTemplate = (rowData) => {
        const timing = rowData.recommendedReminderTiming || {};
        const isReady = timing.timing === 'IMMEDIATE';
        
        return (
            <div className="timing-info">
                <Tag 
                    value={isReady ? 'Ready' : 'Wait'} 
                    severity={isReady ? 'success' : 'warning'} 
                    icon={isReady ? 'pi pi-check-circle' : 'pi pi-clock'}
                />
                <div className="timing-reason">{timing.reason || 'Standard timing'}</div>
            </div>
        );
    }

    outstandingBodyTemplate = (rowData) => {
        const amount = rowData.totalOutstanding || 0;
        return (
            <span className={amount > 0 ? 'outstanding-amount pending' : 'outstanding-amount'}>
                ₹{amount.toLocaleString('en-IN')}
            </span>
        );
    }

    renderContactGroup = (group) => {
        const { students = [] } = group;
        const totalOutstanding = students.reduce((sum, s) => sum + (s.totalOutstanding || 0), 0);
        const readyCount = students.filter(s => s.recommendedReminderTiming?.timing === 'IMMEDIATE').length;

        // Header for Accordion Tab
        const headerContent = (
            <div className="contact-group-header">
                <div className="contact-info-section">
                    <div className="contact-primary">
                        <i className="pi pi-phone"></i>
                        <span className="contact-number">{group.contactNumber}</span>
                    </div>
                    <div className="contact-meta">
                        <span className="parent-name">{group.parentName || 'Unknown'}</span>
                        {group.alternateContactNo && (
                            <span className="alternate-contact">Alt: {group.alternateContactNo}</span>
                        )}
                    </div>
                </div>

                <div className="contact-stats-section">
                    <div className="stat-item">
                        <Badge value={students.length} severity="info" />
                        <span className="stat-label">Students</span>
                    </div>
                    <div className="stat-item">
                        <Badge value={readyCount} severity="success" />
                        <span className="stat-label">Ready</span>
                    </div>
                    <div className="stat-item outstanding">
                        <span className="amount">₹{totalOutstanding.toLocaleString('en-IN')}</span>
                        <span className="stat-label">Outstanding</span>
                    </div>
                </div>
            </div>
        );

        return (
            <AccordionTab key={group.contactNumber} header={headerContent}>
                <DataTable
                    value={students}
                    className="students-table"
                    emptyMessage="No students found"
                >
                    <Column 
                        field="studentName" 
                        header="Student Name" 
                        style={{ minWidth: '150px' }}
                    />
                    <Column 
                        field="admissionNo" 
                        header="Admission No" 
                    />
                    <Column 
                        field="className" 
                        header="Class" 
                    />
                    <Column 
                        header="Profession" 
                        body={this.professionBodyTemplate} 
                        style={{ minWidth: '150px' }}
                    />
                    <Column 
                        header="Cash-Flow" 
                        body={this.cashFlowBodyTemplate} 
                    />
                    <Column 
                        header="Outstanding" 
                        body={this.outstandingBodyTemplate} 
                        style={{ minWidth: '120px' }}
                    />
                    <Column 
                        header="Reminder Status" 
                        body={this.timingBodyTemplate} 
                        style={{ minWidth: '180px' }}
                    />
                </DataTable>
            </AccordionTab>
        );
    }

    render() {
        const { loading, groupedContacts, searchTerm } = this.state;

        if (loading && groupedContacts.length === 0) {
            return <LoadingComponent />;
        }

        // Filter contacts based on search
        const filteredContacts = groupedContacts.filter(group => {
            if (!searchTerm) return true;
            
            const lowerSearch = searchTerm.toLowerCase();
            return (
                group.contactNumber?.includes(searchTerm) ||
                group.parentName?.toLowerCase().includes(lowerSearch) ||
                group.students?.some(s => 
                    s.studentName?.toLowerCase().includes(lowerSearch) ||
                    s.admissionNo?.toLowerCase().includes(lowerSearch)
                )
            );
        });

        const totalContacts = filteredContacts.length;
        const totalStudents = filteredContacts.reduce((sum, g) => sum + g.students.length, 0);
        const totalOutstanding = filteredContacts.reduce((sum, g) => 
            sum + g.students.reduce((s, student) => s + (student.totalOutstanding || 0), 0), 0
        );

        return (
            <div className="primary-contacts">
                <Toast ref={this.toast} position="bottom-right" />

                {/* Header Card */}
                <Card className="header-card">
                    <div className="header-content">
                        <div className="header-left">
                            <h2>Primary Contacts & Multi-Student Families</h2>
                            <p className="description-text">
                                View families with multiple students grouped by contact number
                            </p>
                        </div>
                        <div className="header-stats">
                            <div className="stat-item">
                                <span className="stat-label">Total Contacts</span>
                                <span className="stat-value">{totalContacts}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Total Students</span>
                                <span className="stat-value">{totalStudents}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Total Outstanding</span>
                                <span className="stat-value">₹{totalOutstanding.toLocaleString('en-IN')}</span>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Actions Bar */}
                <Card className="actions-card">
                    <div className="actions-content">
                        <div className="search-section">
                            <span className="p-input-icon-left">
                                <i className="pi pi-search" />
                                <InputText
                                    value={searchTerm}
                                    onChange={(e) => this.setState({ searchTerm: e.target.value })}
                                    placeholder="Search by contact, name, or admission no..."
                                    className="search-input"
                                />
                            </span>
                            {searchTerm && (
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text"
                                    onClick={() => this.setState({ searchTerm: '' })}
                                    tooltip="Clear search"
                                />
                            )}
                        </div>

                        <div className="action-buttons">
                            <Authorizer permId={PERMISSIONS.FDE_PAYER_PROFILES}>
                                <Button
                                    label="Manage Profiles"
                                    icon="pi pi-user-edit"
                                    className="p-button-outlined"
                                    onClick={this.navigateToPayerProfiles}
                                />
                            </Authorizer>
                            <Button
                                label="Refresh"
                                icon="pi pi-refresh"
                                className="p-button-outlined"
                                onClick={this.fetchPrimaryContacts}
                                loading={loading}
                            />
                        </div>
                    </div>
                </Card>

                {/* Contacts Accordion */}
                <Card className="contacts-card">
                    {filteredContacts.length === 0 ? (
                        <div className="empty-state">
                            <i className="pi pi-users" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                            <p>No primary contacts found</p>
                            {searchTerm && (
                                <Button
                                    label="Clear Search"
                                    className="p-button-text"
                                    onClick={() => this.setState({ searchTerm: '' })}
                                />
                            )}
                        </div>
                    ) : (
                        <Accordion
                            activeIndex={this.state.expandedAccordion}
                            onTabChange={this.handleAccordionChange}
                            className="contacts-accordion"
                        >
                            {filteredContacts.map(group => this.renderContactGroup(group))}
                        </Accordion>
                    )}
                </Card>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    userInfo: state?.user?.userInfo || null
});

export default connect(mapStateToProps)(withRouter(PrimaryContacts));
